import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const MessageComposer = () => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('sms');
  const [messageContent, setMessageContent] = useState('');
  const [subject, setSubject] = useState('');
  const [recipientType, setRecipientType] = useState('individual');
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [scheduledTime, setScheduledTime] = useState('');
  const [sending, setSending] = useState(false);

  const templateOptions = [
    { value: 'appointment_reminder', label: 'Appointment Reminder' },
    { value: 'treatment_update', label: 'Treatment Update' },
    { value: 'post_procedure', label: 'Post-Procedure Instructions' },
    { value: 'medication_reminder', label: 'Medication Reminder' },
    { value: 'follow_up', label: 'Follow-Up Care' },
    { value: 'recall_reminder', label: 'Recall Reminder' },
    { value: 'custom', label: 'Custom Message' }
  ];

  const channelOptions = [
    { value: 'sms', label: 'SMS' },
    { value: 'email', label: 'Email' },
    { value: 'both', label: 'SMS + Email' }
  ];

  const recipientOptions = [
    { value: 'individual', label: 'Individual Patient' },
    { value: 'segment', label: 'Patient Segment' },
    { value: 'bulk', label: 'Bulk Send' }
  ];

  const patientOptions = [
    { value: 'p1', label: 'Sarah Johnson' },
    { value: 'p2', label: 'Michael Chen' },
    { value: 'p3', label: 'Emily Rodriguez' },
    { value: 'p4', label: 'David Thompson' },
    { value: 'p5', label: 'Lisa Anderson' }
  ];

  const templateContent = {
    appointment_reminder: 'Hi {patient_name}, this is a reminder of your dental appointment on {appointment_date} at {appointment_time}. Please reply CONFIRM to confirm or call us to reschedule.',
    treatment_update: 'Hello {patient_name}, your treatment plan has been updated. Please review the changes in your patient portal or contact us for details.',
    post_procedure: 'Hi {patient_name}, here are your post-procedure care instructions: {instructions}. If you experience any issues, please contact us immediately.',
    medication_reminder: 'Reminder for {patient_name}: Take your prescribed medication {medication_name} as directed. Refills available - contact us if needed.',
    follow_up: 'Hi {patient_name}, it\'s time for your follow-up appointment. Please call us to schedule at your earliest convenience.',
    recall_reminder: 'Hello {patient_name}, it\'s been 6 months since your last visit. Time for your routine checkup! Call us to schedule.',
    custom: ''
  };

  const handleTemplateChange = (value) => {
    setSelectedTemplate(value);
    setMessageContent(templateContent?.[value] || '');
  };

  const handleSendMessage = async () => {
    setSending(true);
    // Simulate sending
    await new Promise(resolve => setTimeout(resolve, 1500));
    console.log('Sending message:', {
      template: selectedTemplate,
      channel: selectedChannel,
      content: messageContent,
      recipients: selectedPatients,
      scheduledTime
    });
    setSending(false);
    // Reset form
    setMessageContent('');
    setSubject('');
    setSelectedPatients([]);
    setScheduledTime('');
  };

  const characterCount = messageContent?.length || 0;
  const smsLimit = 160;
  const isOverLimit = selectedChannel === 'sms' && characterCount > smsLimit;

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Edit" size={20} className="text-primary" />
          Compose Message
        </h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="Save" iconPosition="left">
            Save Draft
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="Send"
            iconPosition="left"
            onClick={handleSendMessage}
            loading={sending}
            disabled={!messageContent || selectedPatients?.length === 0}
          >
            Send Message
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Template and Channel Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Message Template"
            options={templateOptions}
            value={selectedTemplate}
            onChange={handleTemplateChange}
            placeholder="Select a template"
            required
          />
          <Select
            label="Communication Channel"
            options={channelOptions}
            value={selectedChannel}
            onChange={setSelectedChannel}
            placeholder="Select channel"
            required
          />
        </div>

        {/* Subject Line (Email only) */}
        {(selectedChannel === 'email' || selectedChannel === 'both') && (
          <Input
            label="Subject Line"
            placeholder="Enter email subject"
            value={subject}
            onChange={(e) => setSubject(e?.target?.value)}
            required
          />
        )}

        {/* Message Content */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Message Content <span className="text-destructive">*</span>
          </label>
          <textarea
            className="flex min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="Type your message here... Use {patient_name}, {appointment_date}, {appointment_time} for personalization"
            value={messageContent}
            onChange={(e) => setMessageContent(e?.target?.value)}
          />
          <div className="flex items-center justify-between text-xs">
            <p className="text-muted-foreground">
              Available variables: {'{patient_name}'}, {'{appointment_date}'}, {'{appointment_time}'}
            </p>
            <p className={`font-medium ${
              isOverLimit ? 'text-error' : characterCount > smsLimit * 0.8 ? 'text-warning' : 'text-muted-foreground'
            }`}>
              {characterCount} / {selectedChannel === 'sms' ? `${smsLimit} characters` : 'unlimited'}
            </p>
          </div>
          {isOverLimit && (
            <p className="text-xs text-error">SMS messages over 160 characters will be split into multiple messages</p>
          )}
        </div>

        {/* Recipient Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Recipient Type"
            options={recipientOptions}
            value={recipientType}
            onChange={setRecipientType}
            placeholder="Select recipient type"
            required
          />
          <Select
            label="Select Patients"
            options={patientOptions}
            value={selectedPatients}
            onChange={setSelectedPatients}
            placeholder="Choose patients"
            multiple
            searchable
            required
          />
        </div>

        {/* Scheduling */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            type="datetime-local"
            label="Schedule Send Time (Optional)"
            description="Leave empty to send immediately"
            value={scheduledTime}
            onChange={(e) => setScheduledTime(e?.target?.value)}
          />
        </div>

        {/* Preview Section */}
        <div className="bg-muted/30 rounded-lg p-4 border border-border">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Eye" size={16} className="text-primary" />
            Message Preview
          </h3>
          <div className="bg-background rounded-lg p-4 border border-border">
            {selectedChannel === 'email' && subject && (
              <p className="font-semibold text-foreground mb-2">Subject: {subject}</p>
            )}
            <p className="text-sm text-foreground whitespace-pre-wrap">
              {messageContent || 'Your message will appear here...'}
            </p>
          </div>
          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Icon name={selectedChannel === 'sms' ? 'MessageSquare' : selectedChannel === 'email' ? 'Mail' : 'Send'} size={12} />
              {selectedChannel?.toUpperCase()}
            </span>
            <span>Recipients: {selectedPatients?.length}</span>
            <span>{scheduledTime ? `Scheduled: ${new Date(scheduledTime)?.toLocaleString()}` : 'Send: Immediately'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageComposer;